import { Component, OnInit, Optional, inject, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AuthService } from '../../../../core/services/auth.service';
import { UserRole } from '../../../../core/enums/user-role.enum';

import { NotificationService } from '../../../../core/services/notification.service';
import { SocialAuthService, GoogleLoginProvider, GoogleSigninButtonDirective } from '@abacritt/angularx-social-login';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    GoogleSigninButtonDirective
  ],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
  loginForm: FormGroup;
  showPassword = false;
  private returnUrl = '/';

  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private authService = inject(AuthService);
  private notification = inject(NotificationService);
  private ngZone = inject(NgZone);

  // Usando injeção via construtor com @Optional para evitar que a tela quebre
  private socialAuthService: SocialAuthService | null = null;

  constructor(
    @Optional() socialAuthService: SocialAuthService
  ) {
    this.socialAuthService = socialAuthService;
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]]
    });

    const requestedUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    if (requestedUrl && requestedUrl.startsWith('/')) {
      this.returnUrl = requestedUrl;
    }
  }

  ngOnInit() {
    if (this.route.snapshot.queryParamMap.get('sessionExpired')) {
      this.notification.warn('Sua sessão expirou. Faça login novamente.');
    }

    if (this.socialAuthService) {
      this.socialAuthService.authState.subscribe((user) => {
        if (user && user.idToken) {
          this.ngZone.run(() => {
            this.authService.googleLogin(user.idToken!).subscribe({
              next: () => this.redirectAfterLogin(),
              error: (err) => this.notification.error(err.error?.message || 'Erro ao realizar login com Google')
            });
          });
        }
      });
    }
  }

  togglePassword() {
    this.showPassword = !this.showPassword;
  }

  loginWithGoogle() {
    if (this.socialAuthService) {
      this.socialAuthService.signIn(GoogleLoginProvider.PROVIDER_ID);
    } else {
      this.notification.error('Serviço de login social indisponível no momento.');
    }
  }

  onSubmit() {
    if (this.loginForm.valid) {
      const { email, password } = this.loginForm.value;
      this.authService.login(email, password).subscribe({
        next: () => {
          this.redirectAfterLogin();
        },
        error: (err) => {
          this.notification.error(err.error?.message || 'Erro ao realizar login. Verifique suas credenciais.');
        }
      });
    }
  }

  private redirectAfterLogin() {
    const user = this.authService.getUser();
    const role = user?.role as UserRole | undefined;

    if (role === UserRole.ADMIN) {
      this.router.navigate(['/admin']);
      return;
    }

    if (user && !user.profileComplete) {
      this.router.navigate(['/auth/complete-profile']);
      return;
    }

    if (user && !user.verified) {
      this.router.navigate(['/verification']);
      return;
    }

    if (this.returnUrl && this.returnUrl !== '/auth/login') {
      this.router.navigateByUrl(this.returnUrl);
      return;
    }
    if (role === UserRole.EVENT_ADMIN) {
      this.router.navigate(['/organizer']);
      return;
    }

    this.router.navigate(['/portal']);
  }
}
